const HOUR_TOKEN_REGEX = /(\d+(?:[.,]\d+)?)\s*(?:h|hr|hrs|hour|hours|stunde|stunden)\b/i;
const MINUTE_TOKEN_REGEX = /(\d+)\s*(?:m|min|mins|minute|minutes|minuten)\b/i;
const CLOCK_FORMAT_REGEX = /^(\d+)\s*:\s*([0-5]?\d)$/;
const NUMBER_ONLY_REGEX = /^\d+$/;

export type DurationFilterUnit = "minutes" | "hours";
export type DurationFilterApiUnit = DurationFilterUnit;
export type DurationFilterMode = "exact" | "range";

export interface DurationFilterValue {
  mode?: "exact";
  amount: string;
  unit: DurationFilterUnit;
}

export interface DurationFilterRangeValue {
  mode: "range";
  from: string;
  to: string;
  unit: DurationFilterUnit;
}

export type DurationFilterState = DurationFilterValue | DurationFilterRangeValue;

interface DurationApiValueOptions {
  apiUnit?: DurationFilterApiUnit;
  apiValueTransformer?: (durationInMinutes: number) => string | number;
  rangeParameterSuffixes?: {
    from?: string;
    to?: string;
  };
}

const isDurationFilterValue = (value: unknown): value is DurationFilterValue =>
  Boolean(
    value &&
      typeof value === "object" &&
      !Array.isArray(value) &&
      "amount" in value &&
      "unit" in value,
  );

export const isDurationFilterRangeValue = (
  value: unknown,
): value is DurationFilterRangeValue =>
  Boolean(
    value &&
      typeof value === "object" &&
      !Array.isArray(value) &&
      (value as DurationFilterRangeValue).mode === "range" &&
      "unit" in value,
  );

export const normalizeDurationFilterAmount = (
  amount: unknown,
  unit: DurationFilterUnit,
): string | undefined => {
  const normalizedAmount = String(amount ?? "")
    .trim()
    .replace(",", ".");

  if (normalizedAmount === "") {
    return undefined;
  }

  const amountNumber = Number(normalizedAmount);
  if (!Number.isFinite(amountNumber) || amountNumber < 0) {
    return undefined;
  }

  if (unit === "minutes") {
    return String(Math.round(amountNumber));
  }

  return String(amountNumber);
};

const durationAmountToMinutes = (
  amount: unknown,
  unit: DurationFilterUnit,
): number | undefined => {
  const normalizedAmount = normalizeDurationFilterAmount(amount, unit);
  if (normalizedAmount === undefined) {
    return undefined;
  }

  const amountNumber = Number(normalizedAmount);
  return unit === "hours" ? Math.round(amountNumber * 60) : amountNumber;
};

const convertMinutesForApi = (
  durationInMinutes: number,
  options: DurationApiValueOptions = {},
): string | number => {
  if (options.apiValueTransformer) {
    return options.apiValueTransformer(durationInMinutes);
  }

  if (options.apiUnit === "hours") {
    return String(Number((durationInMinutes / 60).toFixed(4)));
  }

  return String(durationInMinutes);
};

export const parseDurationFilterToMinutes = (
  value: unknown,
): string | undefined => {
  if (value === null || value === undefined) {
    return undefined;
  }

  if (isDurationFilterValue(value)) {
    const minutes = durationAmountToMinutes(value.amount, value.unit);
    if (minutes === undefined) {
      return undefined;
    }

    return String(minutes);
  }

  if (typeof value === "number") {
    return Number.isFinite(value) && value >= 0
      ? String(Math.round(value))
      : undefined;
  }

  const rawValue = String(value).trim().toLowerCase();
  if (rawValue === "") {
    return undefined;
  }

  if (NUMBER_ONLY_REGEX.test(rawValue)) {
    return rawValue;
  }

  const clockMatch = rawValue.match(CLOCK_FORMAT_REGEX);
  if (clockMatch) {
    const hours = Number(clockMatch[1]);
    const minutes = Number(clockMatch[2]);
    return String(hours * 60 + minutes);
  }

  const hourMatch = rawValue.match(HOUR_TOKEN_REGEX);
  const minuteMatch = rawValue.match(MINUTE_TOKEN_REGEX);
  if (!hourMatch && !minuteMatch) {
    return undefined;
  }

  const hours = hourMatch ? Number(hourMatch[1].replace(",", ".")) : 0;
  const minutes = minuteMatch ? Number(minuteMatch[1]) : 0;
  const totalMinutes = Math.round(hours * 60 + minutes);

  return Number.isFinite(totalMinutes) && totalMinutes >= 0
    ? String(totalMinutes)
    : undefined;
};

export const getDurationFilterApiValue = (
  value: unknown,
  options: DurationApiValueOptions = {},
): string | number | undefined => {
  if (isDurationFilterRangeValue(value)) {
    return undefined;
  }

  const minutesValue = parseDurationFilterToMinutes(value);
  if (minutesValue === undefined) {
    return undefined;
  }

  return convertMinutesForApi(Number(minutesValue), options);
};

export const getDurationFilterApiParams = (
  baseKey: string,
  value: unknown,
  options: DurationApiValueOptions = {},
): Record<string, string | number> => {
  if (!isDurationFilterRangeValue(value)) {
    const exactValue = getDurationFilterApiValue(value, options);
    return exactValue === undefined ? {} : { [baseKey]: exactValue };
  }

  const fromMinutes = durationAmountToMinutes(value.from, value.unit);
  const toMinutes = durationAmountToMinutes(value.to, value.unit);
  if (fromMinutes === undefined && toMinutes === undefined) {
    return {};
  }

  const rangeStart =
    fromMinutes !== undefined && toMinutes !== undefined
      ? Math.min(fromMinutes, toMinutes)
      : fromMinutes;
  const rangeEnd =
    fromMinutes !== undefined && toMinutes !== undefined
      ? Math.max(fromMinutes, toMinutes)
      : toMinutes;

  const fromSuffix = options.rangeParameterSuffixes?.from ?? "__gte";
  const toSuffix = options.rangeParameterSuffixes?.to ?? "__lte";
  const params: Record<string, string | number> = {};

  if (rangeStart !== undefined) {
    params[`${baseKey}${fromSuffix}`] = convertMinutesForApi(rangeStart, options);
  }
  if (rangeEnd !== undefined) {
    params[`${baseKey}${toSuffix}`] = convertMinutesForApi(rangeEnd, options);
  }

  return params;
};

export const formatDurationFilterValue = (value: unknown): string => {
  if (isDurationFilterRangeValue(value)) {
    const from = normalizeDurationFilterAmount(value.from, value.unit);
    const to = normalizeDurationFilterAmount(value.to, value.unit);
    const suffix = value.unit === "hours" ? "h" : "m";

    if (from && to) {
      return `${from}${suffix} – ${to}${suffix}`;
    }
    if (from) {
      return `>= ${from}${suffix}`;
    }
    if (to) {
      return `<= ${to}${suffix}`;
    }
    return "";
  }

  if (isDurationFilterValue(value)) {
    const normalizedAmount = normalizeDurationFilterAmount(
      value.amount,
      value.unit,
    );
    if (normalizedAmount === undefined) {
      return "";
    }
    return value.unit === "hours" ? `${normalizedAmount}h` : `${normalizedAmount}m`;
  }

  const minutesValue = parseDurationFilterToMinutes(value);
  if (minutesValue === undefined) {
    return "";
  }

  const totalMinutes = Number(minutesValue);
  if (totalMinutes < 60) {
    return `${totalMinutes}m`;
  }

  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  return minutes ? `${hours}h ${minutes}m` : `${hours}h`;
};
